/**
 * SecureScoreTab: Microsoft Secure Score in the Carbon Security Center
 * layout (mockup 03 patterns, mockup 01 Secure Score tile):
 *   brief           the pillar brief from `composeSecureScoreBrief`, with
 *                   "Open in Microsoft Defender" beside it and the Secure
 *                   Score tile: the percentage, Microsoft's points and the
 *                   30-point grid of what it adds to the Guardian Score
 *   categories      Identity, Data, Apps and Device as framework-style tiles
 *   recommendations a DataTable of the open recommendations, most points
 *                   first, each linking out to its action in Defender
 *   impact vs cost  the open recommendations plotted by cost and points
 *   threats         a DataTable of threat coverage whose rows expand into the
 *                   controls that protect against each threat
 *   services        implemented controls per service as 8px bars
 *
 * Hard preserves: SecureScoreEmptyState, the ref-exposed `refresh()` method,
 * `formatLastUpdated`. There is no sync mutation here: Secure Score is
 * fetched on the Guardian backend schedule, and `fetchedAt` feeds the
 * provenance line.
 */

import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
  type CSSProperties,
} from 'react'
import { useQuery } from '@tanstack/react-query'
import { useMsal } from '@azure/msal-react'

import { api } from '@/lib/api'
import PillarBrief from '@/components/PostureBrief/PillarBrief'
import { SectionHeadline } from '@/components/PostureBrief/SectionHeadline'
import { CategoryBreakdown } from '@/components/SecureScore/CategoryBreakdown'
import { SecureScoreEmptyState } from '@/components/SecureScore/SecureScoreEmptyState'
import { GUARDIAN_WEIGHTS } from '@/components/Dashboard/v2/cc-score'
import { Button, ButtonLink } from '@/components/carbon/Button'
import { DataTable, TableItem, type DataColumn } from '@/components/carbon/DataTable'
import { IconCheck, IconExternal } from '@/components/carbon/icons'
import { InlineNotification } from '@/components/carbon/InlineNotification'
import { PointGrid } from '@/components/carbon/PointGrid'
import { ScoreBar } from '@/components/carbon/ScoreBar'
import { ScoreTile } from '@/components/carbon/ScoreTile'
import { Skeleton } from '@/components/carbon/Skeleton'
import { Tile } from '@/components/carbon/Tile'
import { composeSecureScoreBrief } from '@/lib/security-center/secure-score-brief'
import { aggregateThreats, type ThreatCoverage } from '@/lib/security-center/threat-aggregator'
import { coverageByService } from '@/lib/security-center/service-coverage'
import {
  placeOnMatrix,
  isMatrixClassified,
  X_BY_COST,
  type CostImpactBucket,
  type MatrixTone,
} from '@/lib/security-center/secure-score-matrix'

import type { SecureScoreSummary, SecureScoreControlProfile, SecureScoreResult } from '@/types'

export interface SecureScoreTabProps {
  tenantId: string
  onSummaryChange?: (summary: SecureScoreSummary | null, isRefreshing: boolean) => void
}

export interface SecureScoreTabRef {
  refresh: () => void
}

export function formatLastUpdated(fetchedAt?: string): string {
  if (!fetchedAt) return 'Unknown'
  const date = new Date(fetchedAt)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / (1000 * 60))
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60))

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins} minute${diffMins === 1 ? '' : 's'} ago`
  if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`

  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const DEFENDER_SECURE_SCORE_URL = 'https://security.microsoft.com/securescore'

const gapOf = (r: SecureScoreControlProfile) => Math.max(0, (r.maxScore ?? 0) - (r.achievedScore ?? 0))

const SecureScoreTab = forwardRef<SecureScoreTabRef, SecureScoreTabProps>(
  function SecureScoreTab({ tenantId, onSummaryChange }, ref) {
    const { accounts } = useMsal()
    const userEmail = accounts[0]?.username

    const {
      data: summary,
      isLoading,
      error,
      refetch,
    } = useQuery<SecureScoreSummary | null>({
      queryKey: ['secureScoreSummary', tenantId],
      queryFn: () => api.getSecureScoreSummary(tenantId),
      enabled: !!tenantId,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    })

    // Expose passive refetch to parent. No write mutation — syncs run on
    // the Guardian backend schedule, not on demand from the customer UI.
    useImperativeHandle(ref, () => ({ refresh: () => { refetch() } }), [refetch])

    useEffect(() => {
      if (onSummaryChange) {
        onSummaryChange(summary ?? null, false)
      }
    }, [summary, onSummaryChange])

    /* ------------------------- Brief composition ------------------------- */
    const brief = useMemo(() => {
      const cats = summary?.categoryBreakdown
      const topRecs =
        summary?.recommendations
          ?.filter(r => !r.isImplemented)
          .map(r => ({
            title: r.title,
            gap: gapOf(r),
            category: r.controlCategory,
          }))
          .sort((a, b) => b.gap - a.gap)
          .slice(0, 3) ?? []
      // No `?? 0` fallback here: when `summary.recommendations` hasn't
      // loaded, points-to-gain is unknown, not zero — the composer treats
      // those differently (undefined = "routine fallback" copy, 0 = "clear"
      // copy gated on pct >= 85).
      const totalPtsToGain = summary?.recommendations
        ?.filter(r => !r.isImplemented)
        .reduce((sum, r) => sum + gapOf(r), 0)

      return composeSecureScoreBrief({
        userEmail,
        tenantId,
        score: summary?.score ?? null,
        topRecs,
        // No `?? 0` fallback: Microsoft can omit a category from the
        // breakdown, and coercing that absence to a literal 0 would
        // fabricate a "{Category} is the lowest, at 0%" laggard headline.
        // `weakestCategory` already skips non-number entries.
        categories: cats
          ? {
              identity: cats.identity?.percentage,
              data: cats.data?.percentage,
              apps: cats.apps?.percentage,
              device: cats.device?.percentage,
            }
          : undefined,
        totalPtsToGain,
        delta30d: null, // Phase C: hook into useSecureScoreHistory once available.
        // The provenance line ("Synced by Guardian at 06:12 today").
        syncedAt: summary ? (summary.fetchedAt ?? null) : undefined,
      })
    }, [summary, tenantId, userEmail])

    /* ------------------------- Loading / error / empty ------------------- */
    if (isLoading) return <SecureScoreLoadingSkeleton />

    if (error) {
      return (
        <InlineNotification
          kind="error"
          title="Secure Score did not load."
          className="mt-8"
          actions={
            <Button kind="ghost" size="sm" onClick={() => refetch()}>
              Try again
            </Button>
          }
        >
          {error instanceof Error ? error.message : 'Guardian could not read the Secure Score for this tenant.'}
        </InlineNotification>
      )
    }

    if (!summary || !summary.score || summary.score.maxScore === 0) {
      return <SecureScoreEmptyState tenantId={tenantId} />
    }

    /* ------------------------- Render ------------------------------------ */
    const recs = summary.recommendations ?? []
    const openRecs = recs.filter(r => !r.isImplemented)
    const threats = aggregateThreats(recs)
    const services = coverageByService(recs)

    return (
      <div>
        <PillarBrief
          brief={brief}
          score={summary.score.percentage}
          components={[]}
          tone="bpa"
          scoreLabel="Microsoft Secure Score"
          aside={<SecureScoreTile score={summary.score} />}
          secondaryAction={
            <ButtonLink href={DEFENDER_SECURE_SCORE_URL} kind="tertiary" icon={<IconExternal />}>
              Open in Microsoft Defender
            </ButtonLink>
          }
          footerNote="Microsoft Secure Score is Microsoft's own rating, built from controls across Identity, Data, Apps and Device. Every number and recommendation here comes from **Microsoft 365 Defender**."
        />

        <section className="mt-8" aria-label="Secure Score by category">
          <CategoryBreakdown breakdown={summary.categoryBreakdown} />
        </section>

        <RecommendationsTable recs={openRecs} />

        {openRecs.length > 0 && (
          <section className="mt-12">
            <SectionHeadline level="page">Impact against cost</SectionHeadline>
            <p className="m-0 mt-0.5 max-w-[72ch] text-body text-text-secondary">
              Each open recommendation by implementation cost and points to gain. The top left holds the most points for
              the least work.
            </p>
            <ImpactCostMatrix recs={openRecs} />
          </section>
        )}

        {threats.length > 0 && <ThreatTable threats={threats} recs={recs} />}

        {services.length > 0 && (
          <section className="mt-12">
            <SectionHeadline level="page">Coverage by service</SectionHeadline>
            <p className="m-0 mt-0.5 max-w-[72ch] text-body text-text-secondary">
              Implemented controls out of all controls, per service, as Microsoft categorizes them.
            </p>
            <ServiceCoverage rows={services} />
          </section>
        )}
      </div>
    )
  },
)

export default SecureScoreTab

/* -------------------------------------------------------------------------- */
/* Sub-components                                                             */
/* -------------------------------------------------------------------------- */

/**
 * The Secure Score tile beside the brief (mockup 01): the percentage in the
 * 6rem numeral, Microsoft's points, and the 30-point grid of what Secure
 * Score adds to the Guardian Score (its weight in
 * `calculateUnifiedGuardianScore`), filled in the Secure Score gray.
 */
function SecureScoreTile({ score }: { score: SecureScoreResult }) {
  const weight = GUARDIAN_WEIGHTS.secure
  const pct = Math.max(0, Math.min(100, score.percentage))
  const earned = Math.round((pct * weight) / 100)
  return (
    <ScoreTile
      as="aside"
      className="min-w-0 max-c-lg:max-w-[640px]"
      title="Microsoft Secure Score"
      category="secure"
      score={score.percentage}
      suffix="%"
      meta={
        <>
          <span className="tabular-nums">
            {Math.round(score.currentScore)} of {Math.round(score.maxScore)}
          </span>{' '}
          Microsoft points
        </>
      }
      headingId="secure-score-tile-title"
      visual={
        <>
          <p className="m-0 mb-2 text-helper text-text-helper">
            <span className="tabular-nums">
              {earned} of {weight}
            </span>{' '}
            points toward your Guardian Score
          </p>
          <PointGrid earned={earned} total={weight} label={`${earned} of ${weight} Guardian Score points`} />
        </>
      }
    />
  )
}

/** "+3.5" points, one decimal at most. */
function formatPoints(gap: number): string {
  const rounded = Math.round(gap * 10) / 10
  return `+${Number.isInteger(rounded) ? rounded : rounded.toFixed(1)}`
}

// Defender accepts the controlName (rec.id) as the actionId query param when no
// explicit actionUrl/implementationUrl is published on the profile.
function defenderLinkFor(rec: SecureScoreControlProfile): string {
  return (
    rec.actionUrl
    ?? rec.implementationUrl
    ?? `${DEFENDER_SECURE_SCORE_URL}?viewid=actions&actionId=${encodeURIComponent(rec.id)}`
  )
}

const COST_RANK = { Low: 0, Moderate: 1, High: 2 } as const
const REC_PAGE_SIZE = 10

function RecommendationsTable({ recs }: { recs: SecureScoreControlProfile[] }) {
  const [page, setPage] = useState(1)

  // Sort: gap desc, then cost asc, then title.
  const sorted = useMemo(
    () =>
      [...recs].sort((a, b) => {
        const gA = gapOf(a)
        const gB = gapOf(b)
        if (gA !== gB) return gB - gA
        const cA = COST_RANK[a.implementationCost ?? 'Moderate']
        const cB = COST_RANK[b.implementationCost ?? 'Moderate']
        if (cA !== cB) return cA - cB
        return (a.title ?? '').localeCompare(b.title ?? '')
      }),
    [recs],
  )

  const pages = Math.max(1, Math.ceil(sorted.length / REC_PAGE_SIZE))
  const current = Math.min(page, pages)
  const pageRows = sorted.slice((current - 1) * REC_PAGE_SIZE, current * REC_PAGE_SIZE)

  const columns: DataColumn<SecureScoreControlProfile>[] = [
    {
      id: 'rec',
      header: 'Recommendation',
      primary: true,
      cell: rec => (
        <TableItem
          title={<span className="break-words">{rec.title}</span>}
          sub={[rec.controlCategory, rec.service].filter(Boolean).join(' · ')}
        />
      ),
    },
    {
      id: 'points',
      header: 'Points to gain',
      nowrap: true,
      align: 'end',
      cell: rec => <span className="tabular-nums text-text-primary">{formatPoints(gapOf(rec))}</span>,
    },
    {
      id: 'cost',
      header: 'Cost',
      inlineOnPhone: true,
      cell: rec => `${rec.implementationCost ?? 'Moderate'} cost`,
    },
    {
      id: 'impact',
      header: 'User impact',
      inlineOnPhone: true,
      cell: rec => `${rec.userImpact ?? 'Moderate'} impact`,
    },
    {
      id: 'open',
      header: <span className="sr-only">Action</span>,
      cell: rec => (
        <ButtonLink href={defenderLinkFor(rec)} kind="ghost" size="sm" icon={<IconExternal />} className="max-c-md:-ml-3">
          Defender
          <span className="sr-only">: {rec.title}</span>
        </ButtonLink>
      ),
    },
  ]

  return (
    <div className="mt-12">
      <DataTable
        title="Top recommendations"
        description="Open recommendations from Microsoft, the most points first, then the cheapest to implement."
        columns={columns}
        rows={pageRows}
        rowId={rec => rec.id}
        rowLabel={rec => rec.title}
        empty="Nothing to review."
        pagination={
          sorted.length > REC_PAGE_SIZE
            ? {
                page: current,
                pageSize: REC_PAGE_SIZE,
                total: sorted.length,
                onPageChange: setPage,
                itemLabel: 'recommendations',
              }
            : undefined
        }
      />
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Impact-vs-cost scatter matrix (§7.3)                                       */
/* -------------------------------------------------------------------------- */

/**
 * Marks by user impact. No hue carries it, and three neighboring grays alone read as one series at 10px,
 * so each step also changes the mark: Low is a small outlined square, Moderate a filled square, High a
 * larger filled square in the strongest ink. The key shows the same marks with their counts.
 */
const TONE_MARK: Record<MatrixTone, { size: number; fill: string; stroke: string; strokeWidth: number }> = {
  l: { size: 9, fill: 'none', stroke: 'var(--text-secondary)', strokeWidth: 1.5 },
  m: { size: 11, fill: 'var(--text-secondary)', stroke: 'var(--background)', strokeWidth: 1 },
  h: { size: 15, fill: 'var(--text-primary)', stroke: 'var(--background)', strokeWidth: 1 },
}

const TONE_LABEL: Record<MatrixTone, string> = {
  l: 'Low',
  m: 'Moderate',
  h: 'High',
}

const COST_BUCKETS: readonly CostImpactBucket[] = ['Low', 'Moderate', 'High']
const LEGEND_TONES: readonly MatrixTone[] = ['l', 'm', 'h']

// SVG viewBox geometry. 16:9 plot, padded for axis labels.
const W = 800
const H = 360
const PAD = { top: 24, right: 24, bottom: 44, left: 56 } as const
const PLOT_W = W - PAD.left - PAD.right
const PLOT_H = H - PAD.top - PAD.bottom

const svgText = (fill: string, extra?: CSSProperties): CSSProperties => ({
  fill,
  fontFamily: 'inherit',
  letterSpacing: '0.32px',
  ...extra,
})

function ImpactCostMatrix({ recs }: { recs: SecureScoreControlProfile[] }) {
  const plottable = recs.filter(isMatrixClassified)
  const unclassifiedCount = recs.length - plottable.length

  if (plottable.length === 0) {
    return (
      <Tile className="mt-4">
        <p className="m-0 text-body text-text-secondary">
          Microsoft hasn&rsquo;t classified the cost or impact of these recommendations, so there&rsquo;s nothing to
          plot. They&rsquo;re all in the table above.
        </p>
      </Tile>
    )
  }

  return (
    <Tile className="mt-4">
      <div className="min-w-0 overflow-x-auto">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          className="block h-auto w-full min-w-[560px]"
          role="img"
          aria-label="Recommendations plotted by implementation cost (x) and points to gain (y), shaded by user impact."
        >
          {/* Plot frame and quadrant dividers */}
          <rect x={PAD.left} y={PAD.top} width={PLOT_W} height={PLOT_H} style={{ fill: 'none', stroke: 'var(--border-subtle)' }} />
          <line
            x1={PAD.left + PLOT_W / 2}
            x2={PAD.left + PLOT_W / 2}
            y1={PAD.top}
            y2={PAD.top + PLOT_H}
            strokeDasharray="3 3"
            style={{ stroke: 'var(--border-subtle)' }}
          />
          <line
            x1={PAD.left}
            x2={PAD.left + PLOT_W}
            y1={PAD.top + PLOT_H / 2}
            y2={PAD.top + PLOT_H / 2}
            strokeDasharray="3 3"
            style={{ stroke: 'var(--border-subtle)' }}
          />

          {/* Quadrant labels */}
          <text x={PAD.left + 14} y={PAD.top + 22} fontSize="13" fontWeight="600" style={svgText('var(--text-primary)')}>
            Quick wins
          </text>
          <text
            x={PAD.left + PLOT_W - 14}
            y={PAD.top + 22}
            fontSize="13"
            fontWeight="600"
            textAnchor="end"
            style={svgText('var(--text-primary)')}
          >
            Big strategic moves
          </text>
          <text x={PAD.left + 14} y={PAD.top + PLOT_H - 12} fontSize="12" style={svgText('var(--text-secondary)')}>
            Easy maintenance
          </text>
          <text
            x={PAD.left + PLOT_W - 14}
            y={PAD.top + PLOT_H - 12}
            fontSize="12"
            textAnchor="end"
            style={svgText('var(--text-secondary)')}
          >
            Heavy lift, low payoff
          </text>

          {/* Marks: 10px squares (DESIGN.md "The Square Rule") */}
          {plottable.map((rec) => {
            const p = placeOnMatrix(rec)
            const cost = rec.implementationCost ?? 'Moderate'
            const impact = TONE_LABEL[p.tone]
            const mark = TONE_MARK[p.tone]
            const tooltip = `${rec.title}. Cost ${cost}, user impact ${impact}, ${formatPoints(p.gap)} points. Opens in Microsoft Defender.`
            return (
              <a key={rec.id} href={defenderLinkFor(rec)} target="_blank" rel="noopener noreferrer" aria-label={tooltip}>
                <rect
                  x={PAD.left + p.x * PLOT_W - mark.size / 2}
                  y={PAD.top + p.y * PLOT_H - mark.size / 2}
                  width={mark.size}
                  height={mark.size}
                  style={{ fill: mark.fill, stroke: mark.stroke, strokeWidth: mark.strokeWidth }}
                >
                  <title>{tooltip}</title>
                </rect>
              </a>
            )
          })}

          {/* Axis labels */}
          <text x={PAD.left + PLOT_W / 2} y={H - 10} fontSize="12" textAnchor="middle" style={svgText('var(--text-secondary)')}>
            Implementation cost
          </text>
          <text
            x={-PAD.top - PLOT_H / 2}
            y={16}
            transform="rotate(-90)"
            fontSize="12"
            textAnchor="middle"
            style={svgText('var(--text-secondary)')}
          >
            Points to gain
          </text>

          {/* X-axis ticks */}
          {COST_BUCKETS.map((label) => (
            <text
              key={label}
              x={PAD.left + X_BY_COST[label] * PLOT_W}
              y={PAD.top + PLOT_H + 18}
              fontSize="12"
              textAnchor="middle"
              style={svgText('var(--text-helper)')}
            >
              {label}
            </text>
          ))}
        </svg>
      </div>

      {/* Key */}
      <ul className="m-0 mt-4 flex list-none flex-wrap items-center gap-x-4 gap-y-1 p-0 text-helper text-text-secondary">
        <li className="font-semibold text-text-primary">User impact</li>
        {LEGEND_TONES.map((tone) => {
          const mark = TONE_MARK[tone]
          const count = plottable.filter((rec) => placeOnMatrix(rec).tone === tone).length
          return (
            <li key={tone} className="inline-flex items-center gap-1.5">
              <span
                aria-hidden="true"
                className="box-border flex-none"
                style={{
                  width: mark.size,
                  height: mark.size,
                  background: mark.fill === 'none' ? 'transparent' : mark.fill,
                  border: mark.fill === 'none' ? `${mark.strokeWidth}px solid ${mark.stroke}` : undefined,
                }}
              />
              {TONE_LABEL[tone]} <span className="tabular-nums">({count})</span>
            </li>
          )
        })}
      </ul>

      <p className="m-0 mt-2 text-helper text-text-helper">
        Each mark links to the matching action in Microsoft Defender.
        {unclassifiedCount > 0
          ? unclassifiedCount === 1
            ? ' One recommendation isn’t plotted: Microsoft hasn’t classified its cost or impact.'
            : ` ${unclassifiedCount} recommendations aren’t plotted: Microsoft hasn’t classified their cost or impact.`
          : ''}
      </p>
    </Tile>
  )
}

/* -------------------------------------------------------------------------- */
/* Threat coverage                                                            */
/* -------------------------------------------------------------------------- */

function ThreatTable({ threats, recs }: { threats: ThreatCoverage[]; recs: SecureScoreControlProfile[] }) {
  const columns: DataColumn<ThreatCoverage>[] = [
    {
      id: 'threat',
      header: 'Threat',
      primary: true,
      cell: t => (
        <TableItem
          title={t.threat}
          sub={`${t.implemented} of ${t.total} controls implemented`}
        />
      ),
    },
    {
      id: 'coverage',
      header: 'Coverage',
      width: '240px',
      cell: t => {
        const pct = Math.round(t.coverage * 100)
        return (
          <span className="flex items-center gap-3">
            <span className="w-10 flex-none tabular-nums text-text-primary">{pct}%</span>
            <ScoreBar value={pct} label={`${t.threat} coverage`} className="min-w-[80px] flex-1" />
          </span>
        )
      },
    },
  ]

  return (
    <div className="mt-12">
      <DataTable
        title="Threat coverage"
        description="The threats with the least coverage lead. Expand one for the controls that protect against it."
        columns={columns}
        rows={threats}
        rowId={t => t.threat}
        rowLabel={t => t.threat}
        expandable={{ render: t => <ThreatControls threat={t.threat} recs={recs} /> }}
      />
    </div>
  )
}

function ThreatControls({ threat, recs }: { threat: string; recs: SecureScoreControlProfile[] }) {
  const matching = recs
    .filter((r) => (r.threats ?? []).includes(threat))
    .sort((a, b) => {
      // Open recs (biggest gap first) before implemented.
      if (a.isImplemented !== b.isImplemented) return a.isImplemented ? 1 : -1
      const gA = gapOf(a)
      const gB = gapOf(b)
      if (gA !== gB) return gB - gA
      return (a.title ?? '').localeCompare(b.title ?? '')
    })

  if (matching.length === 0) {
    return <p className="m-0">No matching controls in the current data.</p>
  }

  return (
    <ul aria-label={`Controls for ${threat}`} className="m-0 list-none p-0">
      {matching.map((rec) => {
        const gap = gapOf(rec)
        return (
          <li key={rec.id} className="border-t border-line-subtle first:border-t-0">
            <a
              href={defenderLinkFor(rec)}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex flex-wrap items-center gap-x-3 gap-y-1 py-2 text-text-secondary no-underline"
            >
              <span className="flex w-28 flex-none items-center gap-1.5 text-helper">
                {rec.isImplemented ? (
                  <>
                    <IconCheck size={12} className="text-text-primary" />
                    Implemented
                  </>
                ) : (
                  'Open'
                )}
              </span>
              <span
                className={
                  rec.isImplemented
                    ? 'min-w-0 flex-1 text-text-secondary group-hover:underline'
                    : 'min-w-0 flex-1 font-semibold text-text-primary group-hover:underline'
                }
              >
                {rec.title}
              </span>
              {rec.service ? <span className="flex-none text-helper text-text-helper max-c-md:hidden">{rec.service}</span> : null}
              {!rec.isImplemented && gap > 0 ? (
                <span className="flex-none text-helper tabular-nums text-text-primary">{formatPoints(gap)} points</span>
              ) : null}
              <IconExternal size={12} className="flex-none text-[color:var(--icon-secondary)]" />
              <span className="sr-only"> (opens in Microsoft Defender in a new tab)</span>
            </a>
          </li>
        )
      })}
    </ul>
  )
}

/* -------------------------------------------------------------------------- */
/* Coverage by service                                                        */
/* -------------------------------------------------------------------------- */

function ServiceCoverage({
  rows,
}: {
  rows: ReturnType<typeof coverageByService>
}) {
  return (
    <Tile className="mt-4">
      <ul className="m-0 grid list-none gap-3 p-0">
        {rows.map((row) => (
          <li
            key={row.service}
            className="grid items-center gap-x-4 gap-y-1 text-helper text-text-secondary grid-cols-[minmax(0,1fr)_auto] c-md:grid-cols-[160px_minmax(0,1fr)_120px]"
          >
            <span className="min-w-0 truncate font-semibold text-text-primary">{row.service}</span>
            <ScoreBar value={row.pct} category="secure" label={`${row.service} controls implemented`} className="max-c-md:order-last max-c-md:col-span-2" />
            <span className="text-right tabular-nums">
              {row.pct}% · {row.implemented} of {row.total}
            </span>
          </li>
        ))}
      </ul>
    </Tile>
  )
}

function SecureScoreLoadingSkeleton() {
  return (
    <div className="mt-8 flex flex-col gap-8">
      <div className="grid grid-cols-1 gap-8 c-lg:grid-cols-[minmax(0,1fr)_320px]">
        <Skeleton lines={3} label="Loading Secure Score" />
        <Tile padding={24}>
          <Skeleton lines={4} />
        </Tile>
      </div>
      <Tile>
        <Skeleton lines={5} />
      </Tile>
    </div>
  )
}